import { EComponentId, ComponentPermission } from "../Model/Permission";
import { EUserRole, UserCredential, UserTokenResult } from "../Model/User";
import jwt_decode from "jwt-decode";

const USER_KEY = "user-credential";
const ADMIN_PAGE_PERMISSION = [
  { componentId: EComponentId.SystemOverview, read: true, write: true },
  { componentId: EComponentId.PlateLoadPort, read: true, write: true },
  { componentId: EComponentId.PlateSearch, read: true, write: true },
  { componentId: EComponentId.ImagerLiveImage, read: true, write: true },
  { componentId: EComponentId.ImagerEfi, read: true, write: true },
  { componentId: EComponentId.ImagerDropLocation, read: true, write: true },
  { componentId: EComponentId.ImagerFocusGraph, read: true, write: true },
  { componentId: EComponentId.ImagerLastCaptured, read: true, write: true },
  { componentId: EComponentId.Schedule, read: true, write: true },
  { componentId: EComponentId.Temperature, read: true, write: true },
  {
    componentId: EComponentId.PlateSetupPlateTypeCalibration,
    read: true,
    write: true,
  },
  {
    componentId: EComponentId.PlateSetupDefImgSetting,
    read: true,
    write: true,
  },
  { componentId: EComponentId.PlateSetupImageMask, read: true, write: true },
  { componentId: EComponentId.ToolsStoreAndRetrieve, read: true, write: true },
  { componentId: EComponentId.ToolsGeneral, read: true, write: true },
  { componentId: EComponentId.ToolsEmail, read: true, write: true },
  { componentId: EComponentId.ToolsTesting, read: true, write: true },
  { componentId: EComponentId.SettingsVectorEditor, read: true, write: true },
  { componentId: EComponentId.SettingsImager, read: true, write: true },
  { componentId: EComponentId.SettingsStorage, read: true, write: true },
  { componentId: EComponentId.SettingsPsrCamera, read: true, write: true },
  { componentId: EComponentId.SettingsSystemBackup, read: true, write: true },
  { componentId: EComponentId.SettingsPreferences, read: true, write: true },
  {
    componentId: EComponentId.ReportAndSupportEventLog,
    read: true,
    write: true,
  },
  {
    componentId: EComponentId.ReportAndSupportLicensing,
    read: true,
    write: true,
  },
  {
    componentId: EComponentId.ReportAndSupportUserManagement,
    read: true,
    write: true,
  },
  {
    componentId: EComponentId.ToolsSimulation,
    read: true,
    write: true,
  },
];
const SUPPORT_PAGE_PERMISSION = ADMIN_PAGE_PERMISSION;
const REGULAR_USER_PAGE_PERMISSION = [
  { componentId: EComponentId.SystemOverview, read: true, write: true },
  { componentId: EComponentId.PlateLoadPort, read: true, write: true },
  { componentId: EComponentId.PlateSearch, read: true, write: true },
  { componentId: EComponentId.ImagerLiveImage, read: true, write: true },
  { componentId: EComponentId.ImagerEfi, read: true, write: true },
  { componentId: EComponentId.ImagerDropLocation, read: true, write: true },
  { componentId: EComponentId.ImagerFocusGraph, read: true, write: true },
  { componentId: EComponentId.ImagerLastCaptured, read: true, write: true },
  { componentId: EComponentId.Schedule, read: true, write: true },
  { componentId: EComponentId.Temperature, read: true, write: true },
  {
    componentId: EComponentId.PlateSetupPlateTypeCalibration,
    read: false,
    write: false,
  },
  {
    componentId: EComponentId.PlateSetupDefImgSetting,
    read: false,
    write: false,
  },
  { componentId: EComponentId.PlateSetupImageMask, read: false, write: false },
  {
    componentId: EComponentId.ToolsStoreAndRetrieve,
    read: false,
    write: false,
  },
  { componentId: EComponentId.ToolsGeneral, read: false, write: false },
  { componentId: EComponentId.ToolsEmail, read: false, write: false },
  { componentId: EComponentId.ToolsTesting, read: false, write: false },
  { componentId: EComponentId.SettingsVectorEditor, read: false, write: false },
  { componentId: EComponentId.SettingsImager, read: false, write: false },
  { componentId: EComponentId.SettingsStorage, read: false, write: false },
  { componentId: EComponentId.SettingsPsrCamera, read: false, write: false },
  { componentId: EComponentId.SettingsSystemBackup, read: false, write: false },
  { componentId: EComponentId.SettingsPreferences, read: false, write: false },
  {
    componentId: EComponentId.ReportAndSupportEventLog,
    read: true,
    write: true,
  },
  {
    componentId: EComponentId.ReportAndSupportLicensing,
    read: true,
    write: true,
  },
  {
    componentId: EComponentId.ReportAndSupportUserManagement,
    read: false,
    write: false,
  },
  {
    componentId: EComponentId.ToolsSimulation,
    read: false,
    write: false,
  },
];

class UserCredentialUtil {
  static getUserCredential(): UserCredential | null {
    const userString = localStorage.getItem(USER_KEY);
    let user: UserCredential | null = null;
    if (userString) {
      user = JSON.parse(userString);
    }

    return user;
  }

  static deleteUserCredential() {
    localStorage.removeItem(USER_KEY);
  }

  static setUserCredential(user: UserCredential) {
    let userString = JSON.stringify(user);
    localStorage.setItem(USER_KEY, userString);
  }

  static getUserExpiredTime(user: UserCredential | null) {
    let userFromToken = undefined;
    if (user) {
      userFromToken = jwt_decode<UserTokenResult>(user.token);
    }
    const expiredDate = userFromToken
      ? new Date(userFromToken.exp * 1000)
      : undefined;

    return expiredDate;
  }

  static getUserComponentPermission(user: UserCredential | null) {
    const userRole = user ? (user.role as EUserRole) : EUserRole.None;
    switch (userRole) {
      case EUserRole.Administrator:
        return ADMIN_PAGE_PERMISSION;
      case EUserRole.Support:
        return SUPPORT_PAGE_PERMISSION;
      case EUserRole.Regular:
        return REGULAR_USER_PAGE_PERMISSION;
      case EUserRole.None:
        return [] as ComponentPermission[];
      default:
        return [] as ComponentPermission[];
    }
  }

  static autoLoginPermission() {
    let adminPermission = ADMIN_PAGE_PERMISSION;
    let notAllowedPageList = [
      { componentId: EComponentId.ReportAndSupportUserManagement },
    ];

    let autoLoginPermission: ComponentPermission[] = [];

    notAllowedPageList.forEach((notAllowedComponentId) => {
      autoLoginPermission = UserCredentialUtil.removeSpecificPermission(
        adminPermission,
        notAllowedComponentId.componentId
      );
    });
    return autoLoginPermission;
  }

  static removeSpecificPermission(
    permissions: ComponentPermission[],
    removedComponentId: EComponentId
  ) {
    return permissions.filter(
      (permission) => permission.componentId !== removedComponentId
    );
  }
}

export default UserCredentialUtil;
